import type { GetCannyTokenResponse } from "@nlc/shared-types";

import { isTeamRole } from "@nlc/shared-types";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

import { useUser } from "@/hooks/useUser";
import apiClient from "@/lib/api/client";

import styles from "./make-a-suggestion.module.css";

const CANNY_CUSTOMER_BOARD_TOKEN = "004ca7d6-bdd8-0c93-9c90-7e9b3cfac589";
const CANNY_TEAM_BOARD_TOKEN = "cba50e8c-757a-abe6-3b64-63a5ceeb1492";

declare global {
  interface Window {
    Canny: ((...args: unknown[]) => void) & { q?: unknown[] };
  }
}

export default function MakeASuggestionPage() {
  const { data: userData } = useUser();
  const user = userData?.user;

  const { data: cannyData } = useQuery<GetCannyTokenResponse>({
    enabled: !!user,
    queryFn: () => apiClient.url("/users/canny-token").get().json(),
    queryKey: ["canny-token"],
  });

  const boardToken =
    user?.roleId === undefined
      ? null
      : isTeamRole(user.roleId)
        ? CANNY_TEAM_BOARD_TOKEN
        : CANNY_CUSTOMER_BOARD_TOKEN;

  useEffect(() => {
    if (!cannyData?.token || !boardToken) return;

    if (typeof window.Canny !== "function") {
      const c = (...args: unknown[]) => c.q?.push(args);

      c.q = [] as unknown[][];
      window.Canny = c;
    }

    if (!document.querySelector("#canny-jssdk")) {
      const script = document.createElement("script");

      script.id = "canny-jssdk";
      script.type = "text/javascript";
      script.async = true;
      script.src = "https://sdk.canny.io/sdk.js";
      const firstScript = document.querySelectorAll("script")[0];

      if (firstScript?.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
      }
    }

    window.Canny("render", {
      basePath: null,
      boardToken,
      ssoToken: cannyData.token,
      theme: "light",
    });
  }, [cannyData?.token, boardToken]);

  return (
    <div className={styles.cannyPage}>
      <div className={styles.cannyContainer}>
        <h1 className={styles.cannyTitle}>Submit a feature idea</h1>
        <p className={styles.cannyDescription}>
          Your feedback is invaluable to us, and we see it as a key source of
          inspiration for enhancing our product.
        </p>
        <div className={styles.cannyWidget} data-canny />
      </div>
    </div>
  );
}
